import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usePlaybook, activatePlaybook } from '../../hooks/useSecOps';

interface Step {
  order: number;
  title: string;
  action_type: string;
  description?: string;
}

interface Execution {
  id: string;
  incident_id: string;
  incident_title: string;
  status: string;
  started_at: string;
  completed_at?: string;
}

const STATUS_COLORS: Record<string, string> = {
  running: 'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
  pending: 'bg-gray-100 text-gray-600',
};

function Badge({ value, colors }: { value: string; colors: Record<string, string> }) {
  const label = value.replace(/_/g, ' ');
  return <span className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${colors[value] ?? 'bg-gray-100 text-gray-600'}`}>{label}</span>;
}

export default function PlaybookDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, loading, error, refresh } = usePlaybook(id ?? null);
  const [activating, setActivating] = useState(false);
  const [incidentId, setIncidentId] = useState('');
  const [showActivate, setShowActivate] = useState(false);

  const playbook = data as Record<string, unknown> | null;

  async function handleActivate() {
    if (!id || !incidentId.trim()) return;
    setActivating(true);
    try {
      await activatePlaybook({ playbook_id: id, incident_id: incidentId.trim() });
      setShowActivate(false);
      setIncidentId('');
      refresh();
    } catch { /* handled */ } finally {
      setActivating(false);
    }
  }

  if (loading) {
    return (
      <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-64" />
          <div className="h-64 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  if (error || !playbook) {
    return (
      <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-700 font-medium">Failed to load playbook</p>
          <p className="text-red-500 text-sm mt-1">{error ?? 'Playbook not found'}</p>
          <button onClick={refresh} className="mt-3 text-sm text-indigo-600 hover:text-indigo-800 font-medium">Retry</button>
        </div>
      </div>
    );
  }

  const steps = (playbook.steps as Step[]) ?? [];
  const executions = (playbook.executions as Execution[]) ?? [];

  return (
    <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto space-y-6">
      <button onClick={() => navigate('/secops/playbooks')} className="text-sm text-gray-500 hover:text-gray-700">
        &larr; Back to Playbooks
      </button>

      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{playbook.name as string}</h1>
          <p className="text-sm text-gray-500 mt-1">{(playbook.description as string) || 'No description'}</p>
          <div className="flex items-center gap-3 mt-2">
            <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${playbook.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
              {playbook.active ? 'Active' : 'Inactive'}
            </span>
            <span className="text-sm text-gray-500">Executed {(playbook.execution_count as number) ?? 0} times</span>
          </div>
        </div>
        <button onClick={() => setShowActivate(true)} className="px-3 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700">
          Activate for Incident
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Steps */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-4">Steps ({steps.length})</h2>
            {steps.length === 0 ? (
              <p className="text-sm text-gray-400">No steps defined.</p>
            ) : (
              <div className="space-y-4">
                {steps.sort((a, b) => a.order - b.order).map(step => (
                  <div key={step.order} className="flex gap-4 items-start">
                    <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-bold flex-shrink-0">
                      {step.order}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-gray-900">{step.title}</p>
                        <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded capitalize">{step.action_type.replace(/_/g, ' ')}</span>
                      </div>
                      {step.description && <p className="text-xs text-gray-500 mt-1">{step.description}</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Execution History */}
        <div>
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-4">Execution History</h2>
            {executions.length === 0 ? (
              <p className="text-sm text-gray-400">No executions yet.</p>
            ) : (
              <div className="space-y-3">
                {executions.map(exec => (
                  <div key={exec.id} className="border-l-2 border-gray-200 pl-3">
                    <div className="flex items-center gap-2">
                      <button onClick={() => navigate(`/secops/incidents/${exec.incident_id}`)}
                        className="text-sm text-indigo-600 hover:underline truncate">
                        {exec.incident_title || exec.incident_id}
                      </button>
                      <Badge value={exec.status} colors={STATUS_COLORS} />
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {exec.started_at ? new Date(exec.started_at).toLocaleString() : '-'}
                      {exec.completed_at && ` - ${new Date(exec.completed_at).toLocaleString()}`}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Metadata */}
          <div className="bg-white rounded-lg shadow p-6 mt-6">
            <h2 className="text-base font-semibold text-gray-900 mb-3">Details</h2>
            <div className="space-y-3 text-sm">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase mb-1">Created</p>
                <p className="text-gray-700">{playbook.created_at ? new Date(playbook.created_at as string).toLocaleString() : '-'}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase mb-1">Last Executed</p>
                <p className="text-gray-700">{playbook.last_executed_at ? new Date(playbook.last_executed_at as string).toLocaleString() : 'Never'}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase mb-1">Playbook ID</p>
                <p className="text-xs text-gray-500 font-mono break-all">{playbook.id as string}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Activate Modal */}
      {showActivate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowActivate(false)}>
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Activate Playbook for Incident</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Incident ID</label>
              <input type="text" value={incidentId} onChange={e => setIncidentId(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm" placeholder="Enter incident ID..." />
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setShowActivate(false)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
              <button onClick={handleActivate} disabled={activating || !incidentId.trim()}
                className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50">
                {activating ? 'Activating...' : 'Activate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
